import React from 'react';
import { BasicLayout } from '@/components/BasicLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Status colors
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'received':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'applied':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'pending':
    default:
      return 'bg-red-100 text-red-800 border-red-300';
  }
};

const getStatusBadge = (status) => {
  const color = getStatusColor(status);
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${color}`}>
      {status || 'Pending'}
    </span>
  );
};

function NumberPlateReports({ user, onLogout }) {
  const navigate = useNavigate();
  const [bookings, setBookings] = React.useState([]);
  const [filteredBookings, setFilteredBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedStatuses, setSelectedStatuses] = React.useState(['pending', 'applied', 'received']); // Multi-select
  const [searchValue, setSearchValue] = React.useState('');
  const [dateRange, setDateRange] = React.useState({ from: '', to: '' });
  const [companyDetails, setCompanyDetails] = React.useState(null);
  const [selectedBookings, setSelectedBookings] = React.useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = React.useState(false);
  const [bulkEditStatus, setBulkEditStatus] = React.useState('');
  const [bulkEditInformed, setBulkEditInformed] = React.useState('');
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [exportFields, setExportFields] = React.useState([]);
  // New bulk edit fields for Multi Reports
  const [bulkEditData, setBulkEditData] = React.useState({
    rto_paper_status: '',
    rc_status: '',
    hsrp_number_plate: '',
    hsrp_received_informed: '',
    hsrp_informed_date: '',
    invoice_dms_status: '',
    tally_invoice: '',
    ledger_status: '',
    accounts_verified_by: '',
    exchange_vehicle_status: '',
    loan_status: '',
    pme_drive_no: '',
    pme_status: '',
    exchange_vehicle_no: ''
  });
  const printRef = React.useRef();

  const statusOptions = [
    { id: 'pending', label: 'Pending', color: 'bg-red-100 text-red-800 border-red-300' },
    { id: 'applied', label: 'Applied', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    { id: 'received', label: 'Received', color: 'bg-green-100 text-green-800 border-green-300' },
  ];

  // Export field definitions
  const allExportFields = [
    { id: 'booking_no', label: 'Booking No', default: true },
    { id: 'customer_name', label: 'Customer Name', default: true },
    { id: 'mobile', label: 'Mobile', default: true },
    { id: 'vehicle', label: 'Vehicle', default: true },
    { id: 'chassis_no', label: 'Chassis No', default: true },
    { id: 'registration_number', label: 'Registration No', default: true },
    { id: 'date_of_booking', label: 'Booking Date', default: true },
    { id: 'hsrp_number_plate', label: 'HSRP Status', default: true },
    { id: 'hsrp_received_informed', label: 'Informed', default: true },
    { id: 'dms_invoice_date', label: 'DMS Invoice Date', default: false },
    { id: 'sales_representative', label: 'Sales Rep', default: false },
    { id: 'color', label: 'Color', default: false },
    { id: 'variant', label: 'Variant', default: false },
    { id: 'engine_motor_no', label: 'Engine/Motor No', default: false },
  ];

  const hsrpStatusOptions = ['pending', 'applied', 'received'];
  const informedOptions = ['Yes', 'No'];

  React.useEffect(() => {
    fetchData();
    // Set default export fields
    setExportFields(allExportFields.filter(f => f.default).map(f => f.id));
  }, []);

  React.useEffect(() => {
    filterBookings();
  }, [bookings, selectedStatuses, searchValue, dateRange]);

  const fetchData = async () => {
    try {
      const [bookingsRes, mastersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bookings?limit=10000`),
        axios.get(`${BACKEND_URL}/api/masters?type=company`)
      ]);
      setBookings(bookingsRes.data);
      if (mastersRes.data && mastersRes.data.length > 0) {
        setCompanyDetails(mastersRes.data[0]);
      }
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch data');
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    // Filter by selected statuses (multi-select)
    if (selectedStatuses.length > 0 && selectedStatuses.length < 3) {
      filtered = filtered.filter(b => 
        selectedStatuses.includes((b.hsrp_number_plate || 'pending').toLowerCase())
      );
    }

    // Filter by search
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      filtered = filtered.filter(b =>
        b.booking_no?.toString().includes(search) ||
        b.customer_first_name?.toLowerCase().includes(search) ||
        b.customer_last_name?.toLowerCase().includes(search) ||
        b.mobile_no_1?.includes(search) ||
        b.registration_number?.toLowerCase().includes(search) ||
        b.chassis_no?.toLowerCase().includes(search)
      );
    }

    // Filter by date range
    if (dateRange.from) {
      const fromDate = new Date(dateRange.from);
      filtered = filtered.filter(b => {
        if (!b.date_of_booking) return false;
        return new Date(b.date_of_booking) >= fromDate;
      });
    }
    if (dateRange.to) {
      const toDate = new Date(dateRange.to);
      filtered = filtered.filter(b => {
        if (!b.date_of_booking) return false;
        return new Date(b.date_of_booking) <= toDate;
      });
    }

    // Sort by booking number descending
    filtered.sort((a, b) => {
      const aNum = parseInt(a.booking_no) || 0;
      const bNum = parseInt(b.booking_no) || 0;
      return bNum - aNum;
    });

    setFilteredBookings(filtered);
  };

  const getCounts = () => {
    return {
      all: bookings.length,
      pending: bookings.filter(b => (b.hsrp_number_plate || 'pending').toLowerCase() === 'pending').length,
      applied: bookings.filter(b => (b.hsrp_number_plate || '').toLowerCase() === 'applied').length,
      received: bookings.filter(b => (b.hsrp_number_plate || '').toLowerCase() === 'received').length,
    };
  };

  const counts = getCounts();

  // Status multi-select handler
  const toggleStatus = (statusId) => {
    setSelectedStatuses(prev => {
      if (prev.includes(statusId)) {
        return prev.filter(s => s !== statusId);
      } else {
        return [...prev, statusId];
      }
    });
  };

  // Export field toggle
  const toggleExportField = (fieldId) => {
    setExportFields(prev => {
      if (prev.includes(fieldId)) {
        return prev.filter(f => f !== fieldId);
      } else {
        return [...prev, fieldId];
      }
    });
  };

  const selectAllExportFields = () => {
    setExportFields(allExportFields.map(f => f.id));
  };

  const selectDefaultExportFields = () => {
    setExportFields(allExportFields.filter(f => f.default).map(f => f.id));
  };

  // Selection handlers
  const handleSelectBooking = (id) => {
    setSelectedBookings(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.id));
    }
  };

  // Bulk Edit handler
  const handleBulkEdit = async () => {
    if (selectedBookings.length === 0) {
      toast.error('Please select at least one booking');
      return;
    }

    if (!bulkEditStatus && !bulkEditInformed) {
      toast.error('Please select a status or informed value to update');
      return;
    }

    try {
      toast.info(`Updating ${selectedBookings.length} bookings...`);
      
      // Update HSRP status if selected
      if (bulkEditStatus) {
        await axios.post(`${BACKEND_URL}/api/bookings/bulk-edit`, {
          booking_ids: selectedBookings,
          field: 'hsrp_number_plate',
          value: bulkEditStatus
        });
      }

      // Update HSRP informed if selected
      if (bulkEditInformed) {
        await axios.post(`${BACKEND_URL}/api/bookings/bulk-edit`, {
          booking_ids: selectedBookings,
          field: 'hsrp_received_informed',
          value: bulkEditInformed
        });
      }

      toast.success(`Updated ${selectedBookings.length} bookings successfully!`);
      setShowBulkEditModal(false);
      setSelectedBookings([]);
      setBulkEditStatus('');
      setBulkEditInformed('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update bookings');
    }
  };

  // Inline status update
  const handleInlineStatusUpdate = async (bookingId, newStatus) => {
    try {
      await axios.post(`${BACKEND_URL}/api/bookings/bulk-edit`, {
        booking_ids: [bookingId],
        field: 'hsrp_number_plate',
        value: newStatus
      });
      toast.success('Status updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Get status label for file names
  const getStatusLabel = () => {
    if (selectedStatuses.length === 3) return 'All';
    if (selectedStatuses.length === 0) return 'None';
    return selectedStatuses.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('_');
  };

  // Print handler
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `HSRP_Report_${getStatusLabel()}_${new Date().toISOString().split('T')[0]}`,
  });

  // PDF Download
  const handleDownloadPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const element = printRef.current;
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`HSRP_Report_${getStatusLabel()}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  // Excel Download with selected columns
  const handleDownloadExcel = async () => {
    try {
      toast.info('Generating Excel...');
      const response = await axios.post(`${BACKEND_URL}/api/hsrp-report/export`, {
        booking_ids: filteredBookings.map(b => b.id),
        format: 'excel',
        status_filter: selectedStatuses.join(','),
        fields: exportFields
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HSRP_Report_${getStatusLabel()}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded successfully!');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to generate Excel');
    }
  };

  // Word Download with selected columns
  const handleDownloadWord = async () => {
    try {
      toast.info('Generating Word document...');
      const response = await axios.post(`${BACKEND_URL}/api/hsrp-report/export`, {
        booking_ids: filteredBookings.map(b => b.id),
        format: 'word',
        status_filter: selectedStatuses.join(','),
        fields: exportFields
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HSRP_Report_${getStatusLabel()}_${new Date().toISOString().split('T')[0]}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Word document downloaded successfully!');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to generate Word document');
    }
  };

  // Share handlers
  const handleEmailShare = () => {
    const subject = encodeURIComponent(`HSRP Report - ${getStatusLabel()}`);
    const body = encodeURIComponent(generateShareText());
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(generateShareText());
    { const _a = document.createElement('a'); _a.href = `https://web.whatsapp.com/send?text=${text}`; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
  };

  const generateShareText = () => {
    let text = `📋 HSRP Report - ${getStatusLabel()}\n`;
    text += `📅 Generated: ${formatDate(new Date().toISOString())}\n`;
    text += `📊 Total Records: ${filteredBookings.length}\n\n`;
    
    text += `Summary:\n`;
    text += `• Pending: ${counts.pending}\n`;
    text += `• Applied: ${counts.applied}\n`;
    text += `• Received: ${counts.received}\n\n`;
    
    text += `Recent Records:\n`;
    filteredBookings.slice(0, 10).forEach((b, i) => {
      text += `${i + 1}. ${b.booking_no} - ${b.customer_first_name} ${b.customer_last_name || ''} - ${b.registration_number || 'No Reg'} - ${b.hsrp_number_plate || 'Pending'}\n`;
    });
    
    if (filteredBookings.length > 10) {
      text += `\n... and ${filteredBookings.length - 10} more records`;
    }
    
    return text;
  };

  if (loading) {
    return (
      <BasicLayout user={user} onLogout={onLogout} title="HSRP Report">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </BasicLayout>
    );
  }

  return (
    <BasicLayout user={user} onLogout={onLogout} title="HSRP Report">
      <div className="space-y-6">
        {/* Header with Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`p-4 rounded-xl cursor-pointer transition-all ${selectedStatuses.length === 3 ? 'bg-blue-600 text-white shadow-lg' : 'bg-white border hover:shadow-md'}`}
            onClick={() => setSelectedStatuses(['pending', 'applied', 'received'])}>
            <p className={`text-sm ${selectedStatuses.length === 3 ? 'text-white/80' : 'text-gray-500'}`}>All</p>
            <p className="text-3xl font-bold mt-1">{counts.all}</p>
          </div>
          {statusOptions.map(opt => (
            <div 
              key={opt.id}
              onClick={() => toggleStatus(opt.id)}
              className={`p-4 rounded-xl cursor-pointer transition-all ${
                selectedStatuses.includes(opt.id) && selectedStatuses.length < 3
                  ? opt.id === 'pending' ? 'bg-red-600 text-white shadow-lg' 
                    : opt.id === 'applied' ? 'bg-yellow-500 text-white shadow-lg'
                    : 'bg-green-600 text-white shadow-lg'
                  : 'bg-white border hover:shadow-md'
              }`}
            >
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={selectedStatuses.includes(opt.id)}
                  onChange={() => {}}
                  className="w-4 h-4 rounded"
                />
                <p className={`text-sm ${selectedStatuses.includes(opt.id) && selectedStatuses.length < 3 ? 'text-white/80' : 'text-gray-500'}`}>
                  {opt.label}
                </p>
              </div>
              <p className="text-3xl font-bold mt-1">{counts[opt.id]}</p>
            </div>
          ))}
        </div>

        {/* Filters and Actions */}
        <div className="bg-white p-4 rounded-xl border shadow-sm">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search by name, mobile, booking no, chassis..."
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              />
            </div>

            {/* Clear Filters */}
            {(searchValue || dateRange.from || dateRange.to) && (
              <button
                onClick={() => {
                  setSearchValue('');
                  setDateRange({ from: '', to: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
            {/* Bulk Edit Button */}
            {selectedBookings.length > 0 && (
              <button
                onClick={() => setShowBulkEditModal(true)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Bulk Update ({selectedBookings.length})
              </button>
            )}

            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>

            {/* Download Button - Opens Export Modal */}
            <button 
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export
            </button>

            {/* Share Buttons */}
            <button
              onClick={handleEmailShare}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email
            </button>

            <button
              onClick={handleWhatsAppShare}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </button>
          </div>
        </div>

        {/* Report Table */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden" ref={printRef}>
          {/* Print Header */}
          <div className="p-4 border-b bg-gray-50 print:bg-white">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  HSRP Report - {getStatusLabel()}
                </h2>
                <p className="text-sm text-gray-500">
                  Generated on {formatDate(new Date().toISOString())} | Total: {filteredBookings.length} records
                </p>
              </div>
              {companyDetails && (
                <div className="text-right text-sm text-gray-600">
                  <p className="font-semibold">{companyDetails.name}</p>
                  <p>{companyDetails.mobile}</p>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          {filteredBookings.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-3 font-semibold text-gray-700 print:hidden">
                      <input 
                        type="checkbox" 
                        checked={selectedBookings.length === filteredBookings.length && filteredBookings.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4"
                      />
                    </th>
                    <th className="text-left p-3 font-semibold text-gray-700">S.No</th>
                    <th className="text-left p-3 font-semibold text-gray-700">B.No</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Customer Name</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Mobile</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Vehicle</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Chassis No</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Reg No</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Booking Date</th>
                    <th className="text-left p-3 font-semibold text-gray-700">HSRP Status</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Informed</th>
                    <th className="text-left p-3 font-semibold text-gray-700 print:hidden">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((b, index) => (
                    <tr key={b.id} className={`border-b hover:bg-gray-50 ${selectedBookings.includes(b.id) ? 'bg-blue-50' : ''}`}>
                      <td className="p-3 print:hidden">
                        <input 
                          type="checkbox" 
                          checked={selectedBookings.includes(b.id)}
                          onChange={() => handleSelectBooking(b.id)}
                          className="w-4 h-4"
                        />
                      </td>
                      <td className="p-3 text-gray-500">{index + 1}</td>
                      <td className="p-3 font-medium text-blue-600">{b.booking_no}</td>
                      <td className="p-3">{b.customer_first_name} {b.customer_last_name || ''}</td>
                      <td className="p-3">{b.mobile_no_1 || '-'}</td>
                      <td className="p-3">{b.brand_name} {b.model}</td>
                      <td className="p-3 font-mono text-xs">{b.chassis_no || '-'}</td>
                      <td className="p-3 font-mono text-xs">{b.registration_number || '-'}</td>
                      <td className="p-3">{formatDate(b.date_of_booking)}</td>
                      <td className="p-3">
                        <select
                          value={b.hsrp_number_plate || 'pending'}
                          onChange={(e) => handleInlineStatusUpdate(b.id, e.target.value)}
                          className={`p-1 rounded text-xs font-medium border cursor-pointer ${getStatusColor(b.hsrp_number_plate)} print:border-none print:bg-transparent`}
                        >
                          {hsrpStatusOptions.map(opt => (
                            <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          b.hsrp_received_informed === 'Yes' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {b.hsrp_received_informed || 'No'}
                        </span>
                      </td>
                      <td className="p-3 print:hidden">
                        <button
                          onClick={() => navigate(`/bookings/edit/${b.id}`)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-xs"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No records found matching your criteria</p>
            </div>
          )}

          {/* Summary Footer */}
          <div className="p-4 bg-gray-50 border-t">
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span>Pending: {counts.pending}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                <span>Applied: {counts.applied}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span>Received: {counts.received}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-bold mb-4 text-gray-800">Bulk Update HSRP Status</h3>
            <p className="text-sm text-gray-600 mb-4">
              Updating {selectedBookings.length} selected booking(s)
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HSRP Status</label>
                <select
                  value={bulkEditStatus}
                  onChange={(e) => setBulkEditStatus(e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- No Change --</option>
                  {hsrpStatusOptions.map(opt => (
                    <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HSRP Received Informed</label>
                <select
                  value={bulkEditInformed}
                  onChange={(e) => setBulkEditInformed(e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- No Change --</option>
                  {informedOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleBulkEdit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Update
              </button>
              <button
                onClick={() => {
                  setShowBulkEditModal(false);
                  setBulkEditStatus('');
                  setBulkEditInformed('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-100 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal with Column Selection */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4 text-gray-800">Export HSRP Report</h3>
            <p className="text-sm text-gray-600 mb-4">
              Exporting {filteredBookings.length} records. Select columns to include:
            </p>
            
            {/* Quick Selection Buttons */}
            <div className="flex gap-2 mb-4">
              <button 
                onClick={selectAllExportFields}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                Select All
              </button>
              <button 
                onClick={selectDefaultExportFields}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Reset to Default
              </button>
            </div>
            
            {/* Column Selection Grid */}
            <div className="grid grid-cols-2 gap-2 mb-6 max-h-64 overflow-y-auto border rounded-lg p-3">
              {allExportFields.map(field => (
                <label 
                  key={field.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                    exportFields.includes(field.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  <input 
                    type="checkbox"
                    checked={exportFields.includes(field.id)}
                    onChange={() => toggleExportField(field.id)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm">{field.label}</span>
                </label>
              ))}
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
              {exportFields.length} column(s) selected
            </p>

            {/* Export Format Buttons */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Choose export format:</p>
              <div className="flex gap-2">
                <button
                  onClick={handleDownloadExcel}
                  disabled={exportFields.length === 0}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span>📊</span> Excel
                </button>
                <button
                  onClick={handleDownloadWord}
                  disabled={exportFields.length === 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span>📝</span> Word
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2"
                >
                  <span>📄</span> PDF
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowExportModal(false)}
              className="w-full mt-4 px-4 py-2 border rounded-lg hover:bg-gray-100 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #print-area, #print-area * {
            visibility: visible;
          }
          #print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:bg-white {
            background-color: white !important;
          }
          .print\\:border-none {
            border: none !important;
          }
          .print\\:bg-transparent {
            background-color: transparent !important;
          }
        }
      `}</style>
    </BasicLayout>
  );
}

export default NumberPlateReports;
